// src/services/apiQueryEncoding.ts — make a query string one the api's request
// validator accepts.
//
// The api validates every request against its OpenAPI spec before any route
// runs, and that validator REFUSES a query value holding a raw reserved
// character (: / ? # [ ] @ ! $ & ' ( ) * + , ;), answering 400 "Parameter
// 'search' must be url encoded". The console shows that as "The request didn't
// go through".
//
// Two ordinary ways of building a URL produce exactly that:
//   - `URLSearchParams` writes a SPACE as "+". Searching the KYC register for
//     "sam thomas" sent `search=sam+thomas` and failed every time, while a single
//     name worked (reported by Compliance, 2026-09-28).
//   - `encodeURIComponent` leaves ! ' ( ) * raw, so "O'Brien" fails the same way.
//
// Both are fixed here, once, for every request: "+" in a query string becomes
// %20 and ! ' ( ) * are percent-encoded. Neither changes what the api reads,
// because its query parser decodes "+", %20 and those escapes identically. A
// literal plus is already %2B (both builders encode it), so every raw "+" in a
// query string can only have meant a space.

const QUERY_UNSAFE = /[!'()*]/g;

const percentEncode = (ch: string): string =>
  "%" + ch.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0");

/** Normalise the query part of `url`; the path and any fragment are left alone. */
export function normaliseQueryForApi(url: string): string {
  const q = url.indexOf("?");
  if (q === -1) return url;
  const hash = url.indexOf("#", q);
  const query = hash === -1 ? url.slice(q + 1) : url.slice(q + 1, hash);
  const fragment = hash === -1 ? "" : url.slice(hash);
  const safe = query.replace(/\+/g, "%20").replace(QUERY_UNSAFE, percentEncode);
  return url.slice(0, q + 1) + safe + fragment;
}

type ParamValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Array<string | number | boolean>;

/**
 * Serialise axios `params` the way the api accepts them. axios's own encoder
 * writes a space as "+" and leaves : $ , [ ] raw, all of which the validator
 * refuses. Arrays are comma-joined (encoded), matching the console's other
 * query builders; null and undefined are dropped.
 */
export function serializeApiParams(params: Record<string, ParamValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === null || value === undefined) continue;
    search.append(key, Array.isArray(value) ? value.join(",") : String(value));
  }
  const qs = search.toString();
  return qs ? normaliseQueryForApi("?" + qs).slice(1) : "";
}
